<template>
  <div class="question-rules-configuration">
    <div class="header-section">
      <h1 class="page-title">Question Rules Setup</h1>
      <p class="page-subtitle">
        Define question rules and constraints for categories
      </p>
    </div>

    <div class="main-content">
      <div class="content-layout">
        <!-- Left Panel - Categories Tree -->
        <div class="left-panel">
          <div class="panel-header">
            <h3 class="panel-title">Select Category</h3>
            <p class="panel-hint">
              Choose a category to configure its question rules
            </p>
            <div class="search-box">
              <i class="bi bi-search"></i>
              <input type="text" placeholder="Search Category..." v-model="categorySearchQuery" />
            </div>
          </div>

          <div class="category-tree">
            <div v-for="trait in filteredTraits" :key="trait.id" class="tree-node">
              <!-- Trait node -->
              <div class="trait-node" :class="{
                'has-children': hasCategories(trait.id),
              }">
                <div class="node-content">
                  <i v-if="hasCategories(trait.id)" class="chevron-icon bi" :class="isExpanded(trait.id)
                    ? 'bi-chevron-down'
                    : 'bi-chevron-right'
                    " @click.stop="toggleExpand(trait.id)">
                  </i>

                  <i class="trait-type-icon bi bi-bookmark-fill"></i>

                  <span class="trait-name">{{ trait.name }}</span>
                </div>
                <div class="trait-meta">
                  <span class="categories-count parent-count">
                    {{ getCategoryCount(trait.id) }} categories
                  </span>
                </div>
              </div>

              <!-- Categories under trait -->
              <div v-if="isExpanded(trait.id)" class="children-container">
                <div v-for="category in getCategoriesForTrait(trait.id)" :key="category.id" class="child-wrapper"
                  style="padding-left: 25px">
                  <div class="child-node selectable" :class="{
                    active: selectedCategory?.id === category.id,
                  }" @click="selectCategory(category)">
                    <div class="child-content">
                      <div class="node-header">
                        <div class="tree-lines">
                          <div class="vertical-line"></div>
                          <div class="horizontal-line"></div>
                        </div>

                        <span class="child-name">{{ category.name }}</span>
                      </div>
                      <div class="child-meta">
                        <span class="rules-count selectable-count">
                          {{ getQuestionRulesCount(category.id) }} rules
                        </span>
                        <span class="status-pill" :class="getStatusClass(category.id)">
                          {{ getConfigurationStatus(category.id) }}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Right Panel - Question Rules -->
        <div class="right-panel">
          <div v-if="!selectedCategory" class="empty-state">
            <div class="empty-icon"><i class="bi bi-question-circle"></i></div>
            <h3>Select a Category</h3>
            <p>Choose a category to manage its question rules</p>
            <div class="selection-hint">
              <i class="bi bi-info-circle"></i>
              <span>Question rules define how questions are generated and
                validated</span>
            </div>
          </div>

          <div v-else class="rules-section">
            <!-- Rules Header -->
            <div class="rules-header">
              <div class="header-left">
                <h3 class="rules-title">Question Rules</h3>
                <span class="rules-count">{{ displayedRules.length }} rules configured for
                  {{ selectedCategory.name }}</span>
              </div>
              <div class="header-right">
                <!-- Filter & Actions Section -->
                <div class="filter-actions-container">
                  <!-- <button
                    class="filter-btn"
                    @click="toggleFilterPanel"
                    :class="{ active: showFilterPanel }"
                  >
                    <i class="bi bi-funnel"></i>
                    Filters
                    <span
                      v-if="selectedForFilter.length > 0"
                      class="filter-count"
                      >{{ selectedForFilter.length }}</span
                    >
                  </button> -->
                </div>

                <div class="search-container">
                  <i class="bi bi-search search-icon"></i>
                  <input type="text" class="search-input" placeholder="Search rules..." v-model="rulesSearchQuery" />
                </div>
                <!-- Changed from Add Rule to Create Rule -->
                <button class="add-btn" @click="openCreateRuleModal">
                  <i class="bi bi-plus-circle"></i>
                  Create Rule
                </button>
              </div>
            </div>

            <!-- Table Container -->
            <div class="table-container">
              <table class="rules-table" v-if="displayedRules.length > 0">
                <thead>
                  <tr>
                    <th class="checkbox-col" v-if="showFilterPanel">
                      <input type="checkbox" :checked="allTableItemsSelected" @change="toggleAllTableSelection"
                        class="table-checkbox" />
                    </th>
                    <th class="type-col">Type</th>
                    <th class="difficulty-col">Difficulty Level</th>
                    <th class="questions-col">No. of Questions</th>
                    <th class="fixed-ids-col">Fixed Question IDs</th>
                    <th class="actions-col">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(rule, index) in displayedRules" :key="rule.id" class="rule-row">
                    <td class="checkbox-col" v-if="showFilterPanel">
                      <input type="checkbox" :checked="selectedForFilter.includes(rule.id)"
                        @change="toggleFilterSelection(rule.id)" class="table-checkbox" />
                    </td>
                    <td class="type-col">
                      <div class="type-badges">
                        <span v-for="type in rule.type" :key="type" class="type-badge">
                          {{ type }}
                        </span>
                      </div>
                    </td>
                    <td class="difficulty-col">
                      <span class="difficulty-badge" :class="getDifficultyClass(rule.difficulty)">
                        {{ rule.difficulty }}
                      </span>
                    </td>
                    <td class="questions-col">
                      <span class="questions-count">{{
                        rule.numberOfQuestions
                      }}</span>
                    </td>
                    <td class="fixed-ids-col">
                      <div class="fixed-ids-container">
                        <span v-if="
                          rule.fixedQuestionIds &&
                          rule.fixedQuestionIds.length > 0
                        " class="ids-list">
                          {{ rule.fixedQuestionIds.slice(0, 3).join(", ") }}

                          <span class="ids-count" v-if="rule.fixedQuestionIds.length <= 3">({{
                            rule.fixedQuestionIds.length }})</span>
                          <span class="ids-count" v-if="rule.fixedQuestionIds.length > 3">({{
                            rule.fixedQuestionIds.length - 3 }})</span>
                        </span>
                        <span v-else class="no-ids">No fixed IDs</span>
                      </div>
                    </td>
                    <td class="actions-col">
                      <div class="actions-container">
                        <button class="action-btn edit-btn" @click="editRule(index)" title="Edit">
                          <i class="bi bi-pencil"></i>
                        </button>
                        <button class="action-btn delete-btn" @click="deleteRule(index)" title="Remove">
                          <i class="bi bi-trash"></i>
                        </button>
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>

              <!-- Empty states -->
              <div v-if="displayedRules.length === 0 && !rulesSearchQuery" class="table-empty">
                <div class="empty-icon"><i class="bi bi-plus-circle"></i></div>
                <h4>No Question Rules Added</h4>
                <p>Create question rules for this category to get started</p>
                <!-- Changed to directly call openCreateRuleModal -->
                <button class="add-btn" @click="openCreateRuleModal">
                  <i class="bi bi-plus-circle"></i>
                  Create First Rule
                </button>
              </div>

              <div v-if="displayedRules.length === 0 && rulesSearchQuery" class="table-empty">
                <div class="empty-icon"><i class="bi bi-search"></i></div>
                <h4>No Rules Found</h4>
                <p>
                  No rules match your search criteria: "{{ rulesSearchQuery }}"
                </p>
                <button class="btn-secondary" @click="rulesSearchQuery = ''">
                  Clear Search
                </button>
              </div>
            </div>

            <!-- Table Footer -->
            <div class="table-footer">
              <div class="footer-left">
                <span class="rules-status">
                  <i class="bi bi-check-circle-fill"></i>
                  {{ activeRulesCount }} active rules out of
                  {{ displayedRules.length }} total
                </span>
              </div>
            </div>

            <!-- Configure Section -->
            <div class="configure-section">
              <button class="configure-btn" @click="configureQuestionRules" :disabled="!allCategoriesHaveRules">
                {{
                  allCategoriesHaveRules
                    ? "Complete Question Rules Setup"
                    : `Complete All Categories (${incompleteCategoriesCount} remaining)`
                }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Edit/Create Rule Modal -->
    <div v-if="showModal" class="modal-overlay" @click="closeModal">
      <div class="modal-content" @click.stop ref="modalDialog">
        <div class="modal-header">
          <div class="edit-header-content">
            <h3>{{ modalTitle }}</h3>
          </div>
          <button class="close-btn" @click="closeModal">
            <i class="bi bi-x"></i>
          </button>
        </div>
        <div class="modal-body">
          <!-- Type Field with Multiselect -->
          <div class="form-group">
            <label>Type</label>
            <div class="multiselect-container" ref="typeContainer">
              <div class="selected-questions" @click="toggleTypeDropdown"
                :class="{ 'dropdown-open': showTypeDropdown }">
                <div v-if="ruleForm.type.length > 0" class="selected-items">
                  <span v-for="type in ruleForm.type.slice(0, 3)" :key="type" class="selected-item">
                    {{ type }}
                    <button type="button" @click.stop="removeType(type)" class="remove-item-btn">
                      ×
                    </button>
                  </span>
                  <span v-if="ruleForm.type.length > 3" class="more-count">
                    +{{ ruleForm.type.length - 3 }} more
                  </span>
                </div>
                <span v-else class="placeholder">Select rule types...</span>
                <i class="bi bi-chevron-down dropdown-arrow" :class="{ rotated: showTypeDropdown }"></i>
              </div>

              <div v-if="showTypeDropdown" class="dropdown-menu">
                <div class="dropdown-search">
                  <input type="text" v-model="typeSearchQuery" placeholder="Search types..." class="search-input-small"
                    @click.stop />
                </div>
                <div class="dropdown-options">
                  <div v-for="type in filteredTypes" :key="type.qcid" class="dropdown-option"
                    @click.stop="toggleTypeSelection(type.qcid)">
                    <input type="checkbox" :checked="ruleForm.type.includes(type.qcid)" class="option-checkbox"
                      @change="toggleTypeSelection(type.qcid)" @click.stop />
                    <span class="option-text">{{ type.qcid }} - {{ truncateName(type.name) }}</span>
                  </div>
                  <div v-if="filteredTypes.length === 0" class="no-options">
                    No types found
                  </div>
                </div>
              </div>
            </div>
            <div v-if="typeError" class="field-error">{{ typeError }}</div>
          </div>

          <div class="form-group">
            <label>Difficulty Level</label>
            <select v-model="ruleForm.difficulty" class="modal-input">
              <option value="">Select difficulty level</option>
              <option v-for="option in filteredDifficultyType" :key="option.qdid" :value="option.qdid">
                {{ option.name }}
              </option>
            </select>

            <div v-if="difficultyError" class="field-error">
              {{ difficultyError }}
            </div>
          </div>


          <div class="form-group">
            <label>Number of Questions</label>
            <input type="number" v-model.number="ruleForm.numberOfQuestions"
              :placeholder="`Enter number between 1-${maxQuestions}`" class="modal-input" :min="1"
              :max="maxQuestions" />
            <div class="form-hint">
              Available questions in this category: {{ maxQuestions }}
            </div>
            <div v-if="questionsError" class="field-error">
              {{ questionsError }}
            </div>
          </div>

          <div class="form-group">
            <label>Fixed Question IDs</label>
            <div class="multiselect-container" ref="multiselectContainer">
              <div class="selected-questions" @click="toggleQuestionDropdown"
                :class="{ 'dropdown-open': showQuestionDropdown }">
                <div v-if="ruleForm.fixedQuestionIds.length > 0" class="selected-items">
                  <span v-for="id in ruleForm.fixedQuestionIds.slice(0, 3)" :key="id" class="selected-item">
                    {{ id }}
                    <button type="button" @click.stop="removeQuestionId(id)" class="remove-item-btn">
                      ×
                    </button>
                  </span>
                  <span v-if="ruleForm.fixedQuestionIds.length > 3" class="more-count">
                    +{{ ruleForm.fixedQuestionIds.length - 3 }} more
                  </span>
                </div>
                <span v-else class="placeholder">Select question IDs...</span>
                <i class="bi bi-chevron-down dropdown-arrow" :class="{ rotated: showQuestionDropdown }"></i>
              </div>

              <div v-if="showQuestionDropdown" class="dropdown-menu">
                <div class="dropdown-search">
                  <input type="text" v-model="questionSearchQuery" placeholder="Search question IDs..."
                    class="search-input-small" @click.stop />
                </div>
                <div class="dropdown-options">
                  <div v-for="questionId in filteredQuestionIds" :key="questionId.qid" class="dropdown-option"
                    @click.stop="toggleQuestionSelection(questionId.qid)">
                    <input type="checkbox" :checked="ruleForm.fixedQuestionIds.includes(questionId.qid)"
                      class="option-checkbox" @change="toggleQuestionSelection(questionId.qid)" @click.stop />
                    <span class="option-text">{{ questionId.qid }}</span>
                  </div>
                  <div v-if="filteredQuestionIds.length === 0" class="no-options">
                    No question IDs found
                  </div>
                </div>
              </div>
            </div>
            <div class="form-hint">
              Select specific question IDs to be used for this rule
            </div>
          </div>
        </div>
        <div class="modal-footer">
          <button class="cancel-btn" @click="closeModal">Cancel</button>
          <button class="save-btn" @click="saveRule" :disabled="!canSave">
            {{ editingRule ? "Update" : "Create" }}
          </button>
        </div>
      </div>
    </div>

    <!-- Delete Confirmation Modal -->
    <div v-if="showDeleteModal" class="modal-overlay" @click="closeDeleteModal">
      <div class="modal-content delete-modal" @click.stop>
        <div class="modal-header">
          <h3>Delete Question Rule</h3>
          <button class="close-btn" @click="closeDeleteModal">
            <i class="bi bi-x"></i>
          </button>
        </div>
        <div class="modal-body">
          <div class="delete-content">
            <div class="delete-icon">
              <i class="bi bi-exclamation-triangle"></i>
            </div>
            <div class="delete-message">
              <h4>Are you sure you want to delete this rule?</h4>
              <p>
                <strong>"{{ deletingRule?.name }}"</strong>
              </p>
              <p>
                This action cannot be undone. The rule will be permanently
                removed from the category.
              </p>
            </div>
          </div>
        </div>
        <div class="modal-footer">
          <button class="cancel-btn" @click="closeDeleteModal">Cancel</button>
          <button class="delete-confirm-btn" @click="confirmDeleteRule">
            <i class="bi bi-trash"></i>
            Delete Rule
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions } from 'vuex';
export default {
  name: "QuestionRules",
  props: {
    formData: {
      type: Object,
      default: () => ({
        questionRules: [],
      }),
    },
  },
  emits: ["update-data", "step-completed", "previous-step"],
  data() {
    return {
      selectedCategory: null,
      expandedNodes: [],
      categorySearchQuery: "",
      rulesSearchQuery: "",
      showModal: false,
      showDeleteModal: false,
      showQuestionDropdown: false,
      showTypeDropdown: false,
      questionSearchQuery: "",
      typeSearchQuery: "",
      editingRule: null,
      deletingRule: null,
      traits: [],
      categories: [],

      // Filter Panel
      showFilterPanel: false,
      selectedForFilter: [],

      // Current selected category data from backend
      selectedCategoryData: null,

      // Question rules
      questionRules: [],

      ruleForm: {
        type: [], // Array for multiselect
        difficulty: "",
        numberOfQuestions: null,
        fixedQuestionIds: [],
      },
    };
  },
  computed: {
    filteredTraits() {
      if (!this.categorySearchQuery.trim()) {
        return this.traits.filter((trait) => this.hasCategories(trait.id));
      }

      const query = this.categorySearchQuery.toLowerCase().trim();
      return this.traits.filter((trait) => {
        const matchesTrait = trait.name.toLowerCase().includes(query);
        const hasMatchingCategory = this.getCategoriesForTrait(trait.id).some(
          (category) => category.name.toLowerCase().includes(query)
        );
        return (
          (matchesTrait || hasMatchingCategory) && this.hasCategories(trait.id)
        );
      });
    },

    displayedRules() {
      if (!this.selectedCategory) return [];
      let rules = this.questionRules.filter(
        (rule) => rule.categoryId === this.selectedCategory.id
      );
      if (this.rulesSearchQuery.trim()) {
        const query = this.rulesSearchQuery.toLowerCase().trim();
        rules = rules.filter(
          (rule) =>
            (Array.isArray(rule.type)
              ? rule.type.some((t) =>
                t.toString().toLowerCase().includes(query)
              )
              : rule.type.toString().toLowerCase().includes(query)) ||
            rule.difficulty.toLowerCase().includes(query)
        );
      }
      return rules;
    },

    // Get available types from selected category
    availableTypes() {
      return this.selectedCategoryData?.type || [];
    },

    // Get total questions from selected category
    maxQuestions() {
      return this.selectedCategoryData?.totalNoOfQuestion || 100;
    },

    // Get available question IDs from selected category
    availableQuestionIds() {
      return this.selectedCategoryData?.fixedQuestionIds || [];
    },

    // Filtered types for dropdown
    filteredTypes() {
      if (!this.typeSearchQuery?.trim()) {
        return this.availableTypes;
      }
      const query = this.typeSearchQuery.toLowerCase().trim();
      return this.availableTypes.filter((type) =>
        type.name.toLowerCase().includes(query) ||
        type.qcid.toString().includes(query)
      );
    },

    filteredDifficultyType() {
      const uniqueDifficultyTypeIds = [...new Set(this.selectedCategoryData?.questionDifficulty || [])];
      return uniqueDifficultyTypeIds
    },

    // Filtered question IDs for dropdown
    filteredQuestionIds() {
      const uniqueIds = [...new Set(this.availableQuestionIds)];
      if (!this.questionSearchQuery || !this.questionSearchQuery.trim()) {
        return uniqueIds.sort((a, b) => a - b);
      }

      const query = this.questionSearchQuery.toLowerCase().trim();
      return uniqueIds
        .filter((id) => id.qid.toString().includes(query))
        .sort((a, b) => a - b);
    },

    allTableItemsSelected() {
      return (
        this.displayedRules.length > 0 &&
        this.displayedRules.every((rule) =>
          this.selectedForFilter.includes(rule.id)
        )
      );
    },

    allCategoriesHaveRules() {
      return this.checkAllCategoriesHaveRules().allComplete;
    },

    incompleteCategoriesCount() {
      return this.checkAllCategoriesHaveRules().incompleteCategories.length;
    },

    activeRulesCount() {
      return this.displayedRules.length;
    },

    // Form validation
    typeError() {
      return this.ruleForm.type.length === 0
        ? "Please select at least one rule type"
        : null;
    },

    difficultyError() {
      return !this.ruleForm.difficulty
        ? "Please select a difficulty level"
        : null;
    },

    questionsError() {
      if (!this.ruleForm.numberOfQuestions)
        return "Please enter number of questions";
      if (
        this.ruleForm.numberOfQuestions < 1 ||
        this.ruleForm.numberOfQuestions > this.maxQuestions
      ) {
        return `Number of questions must be between 1 and ${this.maxQuestions}`;
      }
      return null;
    },

    canSave() {
      return (
        this.ruleForm.type.length > 0 &&
        this.ruleForm.difficulty &&
        this.ruleForm.numberOfQuestions &&
        this.ruleForm.numberOfQuestions >= 1 &&
        this.ruleForm.numberOfQuestions <= this.maxQuestions
      );
    },

    // Modal title computed property
    modalTitle() {
      const action = this.editingRule ? "Edit" : "Create";
      const categoryName = this.selectedCategory?.name || "Category";
      return `${action} Question Rule for ${categoryName}`;
    },
  },
  mounted() {
    this.loadData();
    this.autoSelectFirstCategory();
    document.addEventListener("keydown", this.handleEscapeKey);
    document.addEventListener("click", this.handleOutsideClick);
  },
  beforeUnmount() {
    document.removeEventListener("keydown", this.handleEscapeKey);
    document.removeEventListener("click", this.handleOutsideClick);
  },
  methods: {
    // Data loading methods

    ...mapActions([
      "GetCategoryDataByCategoryId"]),

    truncateName(name) {
      return name.length > 50 ? name.substring(0, 50) + '...' : name;
    },

    loadData() {
      console.log("🔄 Loading categories setup data...");

      try {
        const categoriesSetup = localStorage.getItem("categoriesSetup");
        if (categoriesSetup) {
          const data = JSON.parse(categoriesSetup);
          this.traits = data.traits || [];
          this.categories = data.categories || [];
          console.log("✅ Loaded categories setup:", {
            traits: this.traits.length,
            categories: this.categories.length,
          });
        }
      } catch (error) {
        console.warn("Error loading categories setup:", error);
      }

      // Load existing question rules if any
      try {
        const questionRulesConfig = localStorage.getItem("questionRulesSetup");
        if (questionRulesConfig) {
          const data = JSON.parse(questionRulesConfig);
          this.questionRules = data.questionRules || [];
          console.log("✅ Loaded question rules:", this.questionRules.length);
        }
      } catch (error) {
        console.warn("Error loading question rules configuration:", error);
      }

      // Auto-expand all traits that have categories
      this.expandedNodes = this.traits
        .filter((trait) => this.hasCategories(trait.id))
        .map((trait) => trait.id);
    },

    // Tree navigation methods
    hasCategories(traitId) {
      return this.categories.some((category) => category.traitId === traitId);
    },

    getCategoriesForTrait(traitId) {
      return this.categories.filter((category) => category.traitId === traitId);
    },

    getCategoryCount(traitId) {
      return this.getCategoriesForTrait(traitId).length;
    },

    getQuestionRulesCount(categoryId) {
      return this.questionRules.filter((rule) => rule.categoryId === categoryId)
        .length;
    },

    getConfigurationStatus(categoryId) {
      const rulesCount = this.getQuestionRulesCount(categoryId);
      return rulesCount > 0 ? "Configured" : "Not Configured";
    },

    getStatusClass(categoryId) {
      const status = this.getConfigurationStatus(categoryId);
      return {
        "status-configured": status === "Configured",
        "status-not-configured": status === "Not Configured",
      };
    },

    getDifficultyClass(difficulty) {
      const difficultyClasses = {
        Easy: "difficulty-easy",
        Medium: "difficulty-medium",
        Hard: "difficulty-hard",
      };
      return difficultyClasses[difficulty] || "difficulty-default";
    },

    isExpanded(traitId) {
      return this.expandedNodes.includes(traitId);
    },

    toggleExpand(traitId) {
      const index = this.expandedNodes.indexOf(traitId);
      if (index > -1) {
        this.expandedNodes.splice(index, 1);
      } else {
        this.expandedNodes.push(traitId);
      }
    },

    // Updated selectCategory method to fetch category data
    async selectCategory(category) {
      this.selectedCategory = category;
      this.selectedForFilter = [];

      // Simulate fetching category data from backend
      this.selectedCategoryData = await this.fetchCategoryData(category.id);
      this.showNotification(`Selected category: ${category.name}`, "success");
    },

    // Simulate backend API call
    async fetchCategoryData(categoryId) {
      const fetchedData = await this.GetCategoryDataByCategoryId(categoryId)
      const resp = {
        id: categoryId,
        name: "",
        totalNoOfQuestion: fetchedData.object.data.noOfQuestions,
        type: fetchedData.object.data.questionType,
        fixedQuestionIds: fetchedData.object.data.questionMaster,
        questionDifficulty: fetchedData.object.data.questionDifficulty
      }
      return resp
      // console.log("resp::", resp)
      // console.log("fetchedData:::", fetchedData)
      // // Simulate API delay
      // await new Promise((resolve) => setTimeout(resolve, 100));

      // // Return dummy data based on category ID
      // const dummyData = {
      //   1: {
      //     id: 1,
      //     name: "Category A",
      //     totalNoOfQuestion: 45,
      //     type: [1010, 1011, 1012, 1013],
      //     fixedQuestionIds: [12, 120, 242, 121, 342, 34, 67, 89, 90, 234],
      //   },
      //   2: {
      //     id: 2,
      //     name: "Category B",
      //     totalNoOfQuestion: 30,
      //     type: [1015, 1016, 1017],
      //     fixedQuestionIds: [23, 45, 67, 89, 123, 456],
      //   },
      //   3: {
      //     id: 3,
      //     name: "Category C",
      //     totalNoOfQuestion: 60,
      //     type: [1020, 1021, 1022, 1023, 1024],
      //     fixedQuestionIds: [11, 22, 33, 44, 55, 66, 77, 88, 99, 100, 111, 222],
      //   },
      //   4: {
      //     id: 4,
      //     name: "Cate1",
      //     totalNoOfQuestion: 50,
      //     type: [100, 1001, 1010, 120, 1210],
      //     fixedQuestionIds: [12, 120, 242, 121, 342, 121, 34],
      //   },
      // };

      // return (
      //   dummyData[categoryId] || {
      //     id: categoryId,
      //     name: "Default Category",
      //     totalNoOfQuestion: 50,
      //     type: [1010, 1011, 1012],
      //     fixedQuestionIds: [1, 2, 3, 4, 5],
      //   }
      // );
    },

    autoSelectFirstCategory() {
      if (this.categories.length > 0) {
        this.selectCategory(this.categories[0]);
        console.log(
          "✅ Auto-selected first category:",
          this.selectedCategory.name
        );
      }
    },

    handleEscapeKey(event) {
      if (event.key === "Escape") {
        if (this.showModal) {
          this.closeModal();
        } else if (this.showDeleteModal) {
          this.closeDeleteModal();
        } else if (this.showQuestionDropdown) {
          this.showQuestionDropdown = false;
        } else if (this.showTypeDropdown) {
          this.showTypeDropdown = false;
        }
      }
    },

    handleOutsideClick(event) {
      if (this.showQuestionDropdown) {
        const multiselectContainer = this.$refs.multiselectContainer;
        if (
          multiselectContainer &&
          !multiselectContainer.contains(event.target)
        ) {
          this.showQuestionDropdown = false;
        }
      }
      if (this.showTypeDropdown) {
        const typeContainer = this.$refs.typeContainer;
        if (typeContainer && !typeContainer.contains(event.target)) {
          this.showTypeDropdown = false;
        }
      }
    },

    // Type dropdown methods
    toggleTypeDropdown() {
      this.showTypeDropdown = !this.showTypeDropdown;
      if (this.showTypeDropdown) {
        this.typeSearchQuery = "";
        this.$nextTick(() => {
          const searchInput = this.$refs.typeContainer?.querySelector(
            ".search-input-small"
          );
          if (searchInput) {
            searchInput.focus();
          }
        });
      }
    },

    toggleTypeSelection(type) {
      const index = this.ruleForm.type.indexOf(type);
      if (index > -1) {
        this.ruleForm.type.splice(index, 1);
      } else {
        this.ruleForm.type.push(type);
      }
      this.$forceUpdate();
    },

    removeType(type) {
      const index = this.ruleForm.type.indexOf(type);
      if (index > -1) {
        this.ruleForm.type.splice(index, 1);
        this.$forceUpdate();
      }
    },

    // Question selection methods
    toggleQuestionDropdown() {
      console.log(
        "Toggling question dropdown. Current state:",
        this.showQuestionDropdown
      );
      console.log("Current fixedQuestionIds:", this.ruleForm.fixedQuestionIds);
      console.log("Available question IDs:", this.filteredQuestionIds);

      this.showQuestionDropdown = !this.showQuestionDropdown;
      if (this.showQuestionDropdown) {
        this.questionSearchQuery = "";
        // Focus on search input after dropdown opens
        this.$nextTick(() => {
          const searchInput = this.$refs.multiselectContainer?.querySelector(
            ".search-input-small"
          );
          if (searchInput) {
            searchInput.focus();
          }
        });
      }
    },

    removeQuestionId(questionId) {
      console.log("Removing question ID:", questionId);
      const index = this.ruleForm.fixedQuestionIds.indexOf(questionId);
      if (index > -1) {
        this.ruleForm.fixedQuestionIds.splice(index, 1);
        console.log(
          "Removed successfully. Current IDs:",
          this.ruleForm.fixedQuestionIds
        );
        // Force reactivity update
        this.$forceUpdate();
      }
    },

    toggleQuestionSelection(questionId) {
      console.log("Toggling question selection for ID:", questionId);
      console.log(
        "Current fixedQuestionIds before:",
        this.ruleForm.fixedQuestionIds
      );

      const index = this.ruleForm.fixedQuestionIds.indexOf(questionId);
      if (index > -1) {
        this.ruleForm.fixedQuestionIds.splice(index, 1);
        console.log("Removed question ID:", questionId);
      } else {
        this.ruleForm.fixedQuestionIds.push(questionId);
        console.log("Added question ID:", questionId);
      }
      console.log(
        "Current fixedQuestionIds after:",
        this.ruleForm.fixedQuestionIds
      );

      // Force reactivity update
      this.$forceUpdate();
    },

    // Filter panel methods
    toggleFilterPanel() {
      this.showFilterPanel = !this.showFilterPanel;
    },

    closeFilterPanel() {
      this.showFilterPanel = false;
      this.selectedForFilter = [];
    },

    toggleFilterSelection(ruleId) {
      const index = this.selectedForFilter.indexOf(ruleId);
      if (index > -1) {
        this.selectedForFilter.splice(index, 1);
      } else {
        this.selectedForFilter.push(ruleId);
      }
    },

    toggleAllTableSelection() {
      if (this.allTableItemsSelected) {
        this.selectedForFilter = [];
      } else {
        this.selectedForFilter = [...this.displayedRules.map((r) => r.id)];
      }
    },

    removeFromFilter(ruleId) {
      const index = this.selectedForFilter.indexOf(ruleId);
      if (index > -1) {
        this.selectedForFilter.splice(index, 1);
      }
    },

    getRuleName(ruleId) {
      const rule = this.questionRules.find((r) => r.id === ruleId);
      return rule
        ? `Rule ${rule.difficulty} - ${rule.type.join(", ")}`
        : "Unknown";
    },

    // Bulk operations
    bulkToggleStatus() {
      this.showNotification("Bulk operations not implemented yet", "info");
    },

    bulkDeleteRules() {
      if (this.selectedForFilter.length === 0) return;

      if (
        confirm(
          `Are you sure you want to delete ${this.selectedForFilter.length} selected rules? This action cannot be undone.`
        )
      ) {
        this.questionRules = this.questionRules.filter(
          (rule) =>
            !(
              this.selectedForFilter.includes(rule.id) &&
              rule.categoryId === this.selectedCategory.id
            )
        );
        this.showNotification(
          `Deleted ${this.selectedForFilter.length} rules`,
          "warning"
        );
        this.selectedForFilter = [];
        this.saveToLocalStorage();
      }
    },

    // Create Rule Modal
    openCreateRuleModal() {
      if (!this.selectedCategory) {
        this.showNotification("Please select a category first", "warning");
        return;
      }
      this.editingRule = null;
      this.resetForm();
      this.showModal = true;
    },

    deleteRule(index) {
      const rule = this.displayedRules[index];
      this.deletingRule = rule;
      this.showDeleteModal = true;
    },

    closeDeleteModal() {
      this.showDeleteModal = false;
      this.deletingRule = null;
    },

    confirmDeleteRule() {
      if (!this.deletingRule) return;

      const index = this.questionRules.findIndex(
        (r) =>
          r.id === this.deletingRule.id &&
          r.categoryId === this.selectedCategory.id
      );
      if (index > -1) {
        this.questionRules.splice(index, 1);
        this.showNotification(`Rule deleted successfully`, "success");
      }

      this.closeDeleteModal();
      this.saveToLocalStorage();
    },

    // Updated closeModal method
    closeModal() {
      this.showModal = false;
      this.editingRule = null;
      this.showQuestionDropdown = false;
      this.showTypeDropdown = false;
      this.resetForm();
    },

    // Updated resetForm method
    resetForm() {
      this.ruleForm = {
        type: [], // Array for multiselect
        difficulty: "",
        numberOfQuestions: null,
        fixedQuestionIds: [],
      };
      this.questionSearchQuery = "";
      this.typeSearchQuery = "";
    },

    // Updated editRule method
    editRule(index) {
      if (!this.selectedCategory) {
        this.showNotification("Please select a category first", "warning");
        return;
      }
      const rule = this.displayedRules[index];
      const actualIndex = this.questionRules.findIndex(
        (r) => r.id === rule.id && r.categoryId === this.selectedCategory.id
      );

      this.editingRule = this.questionRules[actualIndex];
      this.ruleForm = {
        type: Array.isArray(rule.type) ? [...rule.type] : [rule.type],
        difficulty: rule.difficulty,
        numberOfQuestions: rule.numberOfQuestions,
        fixedQuestionIds: Array.isArray(rule.fixedQuestionIds)
          ? [...rule.fixedQuestionIds]
          : [],
      };
      this.showModal = true;

      this.$nextTick(() => {
        const firstInput = this.$refs.modalDialog?.querySelector("select");
        if (firstInput) firstInput.focus();
      });
    },

    // Updated saveRule method - directly creates rules
    saveRule() {
      if (!this.canSave) {
        this.showNotification(
          "Please fill all required fields correctly",
          "error"
        );
        return;
      }

      if (this.editingRule) {
        // Editing existing rule
        const updatedRule = {
          ...this.editingRule,
          type: [...this.ruleForm.type],
          difficulty: this.ruleForm.difficulty,
          numberOfQuestions: this.ruleForm.numberOfQuestions,
          fixedQuestionIds: [...this.ruleForm.fixedQuestionIds],
          dateModified: new Date().toISOString(),
        };

        const index = this.questionRules.findIndex(
          (r) =>
            r.id === this.editingRule.id &&
            r.categoryId === this.selectedCategory.id
        );
        if (index > -1) {
          this.questionRules.splice(index, 1, updatedRule);
          this.showNotification("Rule updated successfully", "success");
        }
      } else {
        // Creating new rule - directly add to questionRules
        const newRule = {
          id: `rule_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
          categoryId: this.selectedCategory.id,
          type: [...this.ruleForm.type],
          difficulty: this.ruleForm.difficulty,
          numberOfQuestions: this.ruleForm.numberOfQuestions,
          fixedQuestionIds: [...this.ruleForm.fixedQuestionIds],
          dateCreated: new Date().toISOString(),
        };

        this.questionRules.push(newRule);
        this.showNotification("Rule created successfully!", "success");
      }

      this.closeModal();
      this.saveToLocalStorage();
    },

    // Configuration completion
    configureQuestionRules() {
      const categoryRulesCheck = this.checkAllCategoriesHaveRules();

      if (!categoryRulesCheck.allComplete) {
        const incompleteList = categoryRulesCheck.incompleteCategories
          .map((category) => `${category.name} (${category.rulesCount} rules)`)
          .join(", ");

        this.showNotification(
          `Some categories don't have rules configured yet.\n\nIncomplete categories:\n${incompleteList}`,
          "warning"
        );
      }

      const rulesData = {
        questionRules: this.questionRules,
      };

      this.$emit("update-data", {
        stepKey: "questionRulesSetup",
        data: rulesData,
      });

      this.saveToLocalStorage();

      this.showNotification(
        "Question Rules Setup completed! Rules have been configured for categories.",
        "success"
      );
      this.$emit("step-completed", 5);
    },

    checkAllCategoriesHaveRules() {
      const incompleteCategories = [];

      for (const category of this.categories) {
        const categoryRules = this.questionRules.filter(
          (rule) => rule.categoryId === category.id
        );

        if (categoryRules.length === 0) {
          incompleteCategories.push({
            ...category,
            rulesCount: categoryRules.length,
          });
        }
      }

      return {
        allComplete: incompleteCategories.length === 0,
        incompleteCategories,
      };
    },

    saveToLocalStorage() {
      try {
        localStorage.setItem(
          "questionRulesSetup",
          JSON.stringify({
            traits: this.traits,
            categories: this.categories,
            questionRules: this.questionRules,
            lastUpdated: new Date().toISOString(),
          })
        );
      } catch (error) {
        console.error("Error saving to localStorage:", error);
        this.showNotification("Error saving data", "error");
      }
    },

    showNotification(message, type = "info") {
      const alertClass =
        {
          success: "alert-success",
          error: "alert-danger",
          info: "alert-info",
          warning: "alert-warning",
        }[type] || "alert-info";

      const notification = document.createElement("div");
      notification.className = `alert ${alertClass} alert-dismissible fade show position-fixed`;
      notification.style.cssText =
        "top: 20px; right: 20px; z-index: 9999; min-width: 300px;";
      notification.innerHTML = `
        ${message}
        <button type="button" class="btn-close" data-bs-dismiss="alert"></button>
      `;

      document.body.appendChild(notification);

      setTimeout(() => {
        if (notification.parentNode) {
          notification.parentNode.removeChild(notification);
        }
      }, 5000);
    },
  },
};
</script>

<style scoped>
.question-rules-configuration {
  min-height: 100vh;
  background-color: #f8f9fa;
  padding: 2rem;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto",
    sans-serif;
}

.header-section {
  margin-bottom: 2rem;
}

.page-title {
  font-size: 2rem;
  font-weight: 600;
  color: #1f2937;
  margin-bottom: 0.5rem;
}

.page-subtitle {
  font-size: 1rem;
  color: #6b7280;
  margin: 0;
}

.main-content {
  background: white;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
}

.content-layout {
  display: grid;
  grid-template-columns: 350px 1fr;
  min-height: 70vh;
}

/* Left Panel - Categories Tree */
.left-panel {
  background-color: #fafafa;
  border-right: 1px solid #e0e0e0;
}

.panel-header {
  padding: 1.5rem 1.5rem 1rem;
  border-bottom: 1px solid #e0e0e0;
  background-color: white;
}

.panel-title {
  font-size: 1.125rem;
  font-weight: 600;
  color: #2d3748;
  margin-bottom: 0.5rem;
}

.panel-hint {
  font-size: 0.75rem;
  color: #718096;
  margin-bottom: 1rem;
  font-style: italic;
  background: #e6fffa;
  padding: 0.5rem;
  border-radius: 4px;
  border-left: 3px solid #38b2ac;
}

.search-box {
  position: relative;
}

.search-box i {
  position: absolute;
  left: 0.75rem;
  top: 50%;
  transform: translateY(-50%);
  color: #a0aec0;
  font-size: 0.875rem;
}

.search-box input {
  width: 100%;
  padding: 0.75rem 0.75rem 0.75rem 2.25rem;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  font-size: 0.875rem;
  outline: none;
  background-color: white;
  transition: all 0.2s ease;
}

.search-box input:focus {
  border-color: #8b5cf6;
  box-shadow: 0 0 0 3px rgba(139, 92, 246, 0.1);
}

.category-tree {
  padding: 1rem 0;
  max-height: calc(100vh - 200px);
  overflow-y: auto;
}

/* Trait Nodes */
.tree-node {
  margin-bottom: 0.5rem;
}

.trait-node {
  display: flex;
  flex-direction: column;
  margin: 0.5rem 1rem;
  cursor: default;
  transition: all 0.2s ease;
  border-radius: 8px;
  background-color: white;
  border: 1px solid #e2e8f0;
}

.trait-node.has-children:hover {
  background-color: #f7fafc;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}

.node-content {
  display: flex;
  align-items: center;
  width: 100%;
  padding: 0.75rem 1rem;
}

.trait-meta {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0 1rem 0.75rem 1rem;
  margin-top: -0.25rem;
}

.chevron-icon {
  margin-right: 0.75rem;
  font-size: 0.875rem;
  color: #718096;
  transition: all 0.2s ease;
  width: 16px;
  text-align: center;
  cursor: pointer;
}

.chevron-icon:hover {
  color: #8b5cf6;
}

.trait-type-icon {
  margin-right: 0.5rem;
  font-size: 0.875rem;
  color: #f56500;
  opacity: 0.8;
}

.trait-name {
  font-weight: 600;
  font-size: 0.95rem;
  flex: 1;
  color: #4a5568;
}

.parent-count {
  color: #f56500;
  font-weight: 500;
  font-size: 0.75rem;
}

.categories-count {
  font-size: 0.75rem;
  color: #718096;
  background-color: #edf2f7;
  padding: 0.25rem 0.5rem;
  border-radius: 12px;
}

/* Child Nodes (Categories) */
.children-container {
  position: relative;
  margin-left: 1rem;
}

.children-container::before {
  content: "";
  position: absolute;
  left: 25px;
  top: 0;
  bottom: 0;
  width: 1px;
  background: #e2e8f0;
}

.child-wrapper {
  position: relative;
}

.child-node {
  margin-bottom: 0.75rem;
  cursor: pointer;
  transition: all 0.2s ease;
  border-radius: 8px;
  background-color: white;
  border: 1px solid #e2e8f0;
}

.child-node.selectable:hover {
  background-color: #f0fff4;
  border-color: #48bb78;
  transform: translateX(2px);
}

.child-node.selectable.active {
  background-color: #48bb78;
  color: white;
  box-shadow: 0 4px 12px rgba(72, 187, 120, 0.3);
  border-color: #38a169;
}

.child-content {
  padding: 0.75rem 1rem;
  display: flex;
  flex-direction: column;
}

.node-header {
  display: flex;
  align-items: center;
  width: 100%;
}

.child-meta {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 0.5rem;
  font-size: 0.8rem;
}

.tree-lines {
  position: relative;
  width: 20px;
  height: 20px;
  margin-right: 0.5rem;
}

.vertical-line {
  position: absolute;
  left: 10px;
  top: -10px;
  width: 1px;
  height: 30px;
  background: #e2e8f0;
}

.horizontal-line {
  position: absolute;
  left: 10px;
  top: 10px;
  width: 10px;
  height: 1px;
  background: #e2e8f0;
}

.child-name {
  font-weight: 500;
  font-size: 0.9rem;
  color: #4a5568;
  flex: 1;
}

.child-node.active .child-name {
  color: white;
}

.selectable-count {
  color: #22543d;
  font-weight: 500;
  background-color: #c6f6d5;
  padding: 0.125rem 0.375rem;
  border-radius: 8px;
  font-size: 0.7rem;
}

.child-node.active .categories-count,
.child-node.active .selectable-count {
  background-color: rgba(255, 255, 255, 0.2);
  color: white;
}

.rules-count {
  color: #22543d;
  font-weight: 500;
  background-color: #c6f6d5;
  padding: 0.125rem 0.375rem;
  border-radius: 8px;
  font-size: 0.7rem;
}

/* Status Pills */
.status-pill {
  padding: 0.125rem 0.375rem;
  border-radius: 8px;
  font-size: 0.7rem;
  font-weight: 600;
  color: white;
  margin-left: 0.5rem;
}

.status-configured {
  background-color: #48bb78;
}

.status-inactive {
  background-color: #ed8936;
}

.status-not-configured {
  background-color: #fc8181;
}

/* Right Panel - Question Rules */
.right-panel {
  background-color: white;
  display: flex;
  flex-direction: column;
}

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 4rem 2rem;
  color: #718096;
}

.empty-state .empty-icon {
  font-size: 3rem;
  margin-bottom: 1rem;
  color: #cbd5e0;
}

.empty-state h3 {
  font-size: 1.5rem;
  font-weight: 600;
  color: #2d3748;
  margin-bottom: 0.5rem;
}

.empty-state h4 {
  font-size: 1.25rem;
  font-weight: 600;
  color: #2d3748;
  margin-bottom: 0.5rem;
}

.empty-state p {
  font-size: 1rem;
  color: #718096;
}

.selection-hint {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-top: 1rem;
  padding: 0.75rem;
  background: #e6fffa;
  border-radius: 6px;
  color: #22543d;
  font-size: 0.875rem;
}

.selection-hint i {
  color: #38b2ac;
}

.rules-section {
  display: flex;
  flex-direction: column;
  height: 100%;
}

.rules-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.5rem 2rem 1rem;
  border-bottom: 1px solid #e2e8f0;
  background-color: #fafafa;
}

.header-left {
  display: flex;
  flex-direction: column;
}

.rules-title {
  font-size: 1.25rem;
  font-weight: 600;
  color: #2d3748;
  margin-bottom: 0.25rem;
}

.rules-count {
  font-size: 0.875rem;
  color: #718096;
}

.header-right {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.search-container {
  position: relative;
}

.search-icon {
  position: absolute;
  left: 0.75rem;
  top: 50%;
  transform: translateY(-50%);
  color: #a0aec0;
  font-size: 0.875rem;
}

.search-input {
  padding: 0.5rem 0.75rem 0.5rem 2.25rem;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  font-size: 0.875rem;
  outline: none;
  width: 250px;
  transition: all 0.2s ease;
}

.search-input:focus {
  border-color: #8b5cf6;
  box-shadow: 0 0 0 3px rgba(139, 92, 246, 0.1);
}

.add-btn {
  background: linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%);
  color: white;
  border: none;
  padding: 0.6rem 1.25rem;
  border-radius: 8px;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  box-shadow: 0 2px 4px rgba(139, 92, 246, 0.2);
}

.add-btn:hover {
  background: linear-gradient(135deg, #7c3aed 0%, #6d28d9 100%);
  transform: translateY(-1px);
  box-shadow: 0 4px 8px rgba(139, 92, 246, 0.3);
}

/* Table Styles */
.table-container {
  flex: 1;
  overflow-x: auto;
  background: white;
  padding: 1rem 2rem;
}

.rules-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.875rem;
}

.rules-table th {
  background: #f8f9fa;
  padding: 1rem;
  text-align: left;
  font-weight: 600;
  color: #4a5568;
  border-bottom: 2px solid #e2e8f0;
  position: sticky;
  top: 0;
  z-index: 10;
}

.rules-table td {
  padding: 1rem;
  border-bottom: 1px solid #e2e8f0;
  vertical-align: middle;
}

.rule-row {
  transition: all 0.2s ease;
}

.rule-row:hover {
  background-color: #f7fafc;
}

.rule-row.inactive {
  background-color: #fef5e7;
  opacity: 0.8;
}

.type-col {
  width: 140px;
}

.difficulty-col {
  width: 120px;
  text-align: center;
}

.questions-col {
  width: 100px;
  text-align: center;
}

.fixed-ids-col {
  width: 200px;
}

.actions-col {
  width: 160px;
  text-align: center;
}

/* Type badges for array display */
.type-badges {
  display: flex;
  flex-wrap: wrap;
  gap: 0.25rem;
}

.type-badge {
  padding: 0.25rem 0.5rem;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 600;
  color: white;
  background-color: #8b5cf6;
}

/* Difficulty badges */
.difficulty-badge {
  padding: 0.25rem 0.5rem;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 600;
  color: white;
}

.difficulty-easy {
  background-color: #48bb78;
}

.difficulty-medium {
  background-color: #ed8936;
}

.difficulty-hard {
  background-color: #e53e3e;
}

.difficulty-default {
  background-color: #718096;
}

/* Questions count */
.questions-count {
  font-weight: 600;
  color: #2d3748;
  font-size: 1rem;
}

/* Fixed IDs container */
.fixed-ids-container {
  font-size: 0.875rem;
}

.ids-list {
  color: #4a5568;
  line-height: 1.4;
}

.ids-count {
  color: #718096;
  font-size: 0.75rem;
  margin-left: 0.25rem;
}

.no-ids {
  color: #cbd5e0;
  font-style: italic;
}

.actions-container {
  display: flex;
  gap: 0.25rem;
  justify-content: center;
}

.action-btn {
  background: none;
  border: 1px solid #e2e8f0;
  color: #718096;
  padding: 0.375rem;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
}

.edit-btn:hover {
  background: #8b5cf6;
  color: white;
  border-color: #8b5cf6;
}

.delete-btn:hover {
  background: #e53e3e;
  color: white;
  border-color: #e53e3e;
}

.table-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 4rem 2rem;
  color: #718096;
}

.table-empty .empty-icon {
  font-size: 3rem;
  margin-bottom: 1rem;
  color: #cbd5e0;
}

.table-empty h4 {
  font-size: 1.25rem;
  font-weight: 600;
  color: #2d3748;
  margin-bottom: 0.5rem;
}

.table-empty p {
  font-size: 1rem;
  color: #718096;
  margin-bottom: 1.5rem;
}

.btn-secondary {
  background: #e2e8f0;
  color: #4a5568;
  border: none;
  padding: 0.5rem 1rem;
  border-radius: 6px;
  font-size: 0.875rem;
  cursor: pointer;
  transition: all 0.2s ease;
}

.btn-secondary:hover {
  background: #cbd5e0;
}

/* Table Footer */
.table-footer {
  padding: 1rem 2rem;
  border-top: 1px solid #e2e8f0;
  background: #fafafa;
}

.footer-left {
  display: flex;
  align-items: center;
}

.rules-status {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.875rem;
  font-weight: 500;
  color: #22543d;
}

.rules-status i {
  color: #48bb78;
}

/* Configure Section */
.configure-section {
  padding: 1.5rem 2rem;
  background: white;
  border-top: 1px solid #e2e8f0;
}

.configure-btn {
  background: linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%);
  color: white;
  border: none;
  padding: 0.875rem 2rem;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  width: 100%;
  box-shadow: 0 2px 4px rgba(139, 92, 246, 0.2);
}

.configure-btn:hover:not(:disabled) {
  background: linear-gradient(135deg, #7c3aed 0%, #6d28d9 100%);
  transform: translateY(-1px);
  box-shadow: 0 4px 8px rgba(139, 92, 246, 0.3);
}

.configure-btn:disabled {
  background: #a0aec0;
  cursor: not-allowed;
  transform: none;
  box-shadow: none;
}

/* MODAL STYLES */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  backdrop-filter: blur(4px);
  animation: fadeIn 0.2s ease-out;
  overflow: hidden;
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }

  to {
    opacity: 1;
  }
}

.modal-content {
  background: white;
  border-radius: 12px;
  width: 100%;
  max-width: 480px;
  max-height: 80vh;
  overflow: hidden;
  box-shadow: 0 20px 40px -10px rgba(0, 0, 0, 0.2);
  display: flex;
  flex-direction: column;
  position: relative;
  z-index: 1001;
  animation: slideIn 0.3s ease-out;
  margin: 1rem;
}

@keyframes slideIn {
  from {
    opacity: 0;
    transform: translateY(-20px) scale(0.95);
  }

  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

.delete-modal {
  max-width: 400px;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: 1.25rem 1.5rem 1rem;
  border-bottom: 1px solid #e2e8f0;
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
  border-radius: 12px 12px 0 0;
  flex-shrink: 0;
}

.modal-header h3 {
  margin: 0;
  font-size: 1.125rem;
  font-weight: 700;
  color: #1e293b;
  background: linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.edit-header-content {
  flex: 1;
}

.close-btn {
  background: none;
  border: none;
  color: #64748b;
  font-size: 1.25rem;
  cursor: pointer;
  padding: 0.375rem;
  border-radius: 6px;
  transition: all 0.2s ease;
  margin-top: -0.125rem;
}

.close-btn:hover {
  background: #f1f5f9;
  color: #1e293b;
  transform: scale(1.1);
}

.modal-body {
  flex: 1;
  padding: 1.25rem 1.5rem;
  overflow-y: auto;
  overflow-x: hidden;
  position: relative;
  z-index: 1002;
  background: #fafafa;
  max-height: calc(80vh - 180px);
  isolation: isolate;
}

/* FORM STYLES */
.form-group {
  margin-bottom: 1.25rem;
  position: relative;
}

.form-group:last-child {
  margin-bottom: 0;
}

.form-group label {
  display: block;
  margin-bottom: 0.5rem;
  font-weight: 600;
  color: #374151;
  font-size: 0.8rem;
  text-transform: uppercase;
  letter-spacing: 0.03em;
}

.form-group label::after {
  content: "*";
  color: #ef4444;
  margin-left: 0.25rem;
  font-weight: bold;
}

.form-group:has(.form-hint) label::after,
.form-group:has(label[for*="fixed"]) label::after {
  content: "";
}

.modal-input {
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  font-size: 0.8rem;
  outline: none;
  transition: all 0.2s ease;
  background: white;
  font-family: inherit;
}

.modal-input:focus {
  border-color: #8b5cf6;
  box-shadow: 0 0 0 3px rgba(139, 92, 246, 0.1);
}

.modal-input::placeholder {
  color: #9ca3af;
}

.field-error {
  margin-top: 0.375rem;
  font-size: 0.75rem;
  color: #ef4444;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 0.25rem;
}

.field-error::before {
  content: "⚠";
  font-size: 0.8rem;
}

.form-hint {
  margin-top: 0.375rem;
  font-size: 0.7rem;
  color: #6b7280;
  font-style: italic;
  background: #f3f4f6;
  padding: 0.5rem;
  border-radius: 4px;
  border-left: 3px solid #8b5cf6;
}

/* MULTISELECT STYLES */
.multiselect-container {
  position: relative;
  z-index: 10;
}

.selected-questions {
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  padding: 0.75rem;
  background: white;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-height: 42px;
  transition: all 0.2s ease;
  position: relative;
  user-select: none;
}

.selected-questions:hover {
  border-color: #8b5cf6;
}

.selected-questions.dropdown-open {
  border-color: #8b5cf6;
  box-shadow: 0 0 0 3px rgba(139, 92, 246, 0.1);
  border-bottom-left-radius: 0;
  border-bottom-right-radius: 0;
}

.dropdown-menu {
  position: absolute !important;
  top: 100% !important;
  left: 0 !important;
  right: 0 !important;
  background: white !important;
  border: 1px solid #8b5cf6 !important;
  border-top: none !important;
  border-radius: 0 0 6px 6px !important;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15) !important;
  z-index: 50 !important;
  max-height: 180px !important;
  overflow: hidden !important;
  display: block !important;
  opacity: 1 !important;
  visibility: visible !important;
  animation: dropdownSlide 0.2s ease-out !important;
  max-width: 100% !important;
}

@keyframes dropdownSlide {
  from {
    opacity: 0;
    transform: translateY(-4px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.dropdown-search {
  padding: 0.75rem !important;
  border-bottom: 1px solid #e5e7eb !important;
  background: #f8fafc !important;
  position: sticky !important;
  top: 0 !important;
  z-index: 51 !important;
}

.search-input-small {
  width: 100% !important;
  padding: 0.5rem 0.75rem !important;
  border: 1px solid #d1d5db !important;
  border-radius: 4px !important;
  font-size: 0.75rem !important;
  outline: none !important;
  transition: all 0.2s ease !important;
  background: white !important;
}

.search-input-small:focus {
  border-color: #8b5cf6 !important;
  box-shadow: 0 0 0 2px rgba(139, 92, 246, 0.1) !important;
}

.dropdown-options {
  max-height: 120px !important;
  overflow-y: auto !important;
  background: white !important;
  position: relative !important;
  z-index: 49 !important;
}

.dropdown-option {
  padding: 0.625rem 0.75rem !important;
  cursor: pointer !important;
  display: flex !important;
  align-items: center !important;
  gap: 0.5rem !important;
  transition: all 0.2s ease !important;
  border-bottom: 1px solid #f3f4f6 !important;
  background: white !important;
  font-size: 0.75rem !important;
}

.dropdown-option:last-child {
  border-bottom: none !important;
}

.dropdown-option:hover {
  background: #f8fafc !important;
}

.option-checkbox {
  margin: 0 !important;
  width: 14px !important;
  height: 14px !important;
  cursor: pointer !important;
  flex-shrink: 0 !important;
  accent-color: #8b5cf6 !important;
}

.option-text {
  font-size: 0.75rem !important;
  color: #374151 !important;
  flex: 1 !important;
  line-height: 1.3 !important;
  word-break: break-word !important;
  font-weight: 500 !important;
}

.no-options {
  padding: 1.5rem 1rem !important;
  text-align: center !important;
  color: #6b7280 !important;
  font-style: italic !important;
  font-size: 0.75rem !important;
  background: #f9fafb !important;
}

.selected-items {
  display: flex;
  flex-wrap: wrap;
  gap: 0.375rem;
  flex: 1;
  align-items: center;
}

.selected-item {
  background: linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%);
  color: white;
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  font-size: 0.7rem;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 0.25rem;
  max-width: 100px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  box-shadow: 0 1px 3px rgba(139, 92, 246, 0.2);
}

.remove-item-btn {
  background: none;
  border: none;
  color: white;
  cursor: pointer;
  font-size: 0.875rem;
  font-weight: bold;
  padding: 0 0.125rem;
  line-height: 1;
  opacity: 0.8;
  transition: all 0.2s ease;
  border-radius: 2px;
}

.remove-item-btn:hover {
  opacity: 1;
  background: rgba(255, 255, 255, 0.2);
}

.more-count {
  background: #e5e7eb;
  color: #374151;
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  font-size: 0.7rem;
  font-weight: 600;
}

.placeholder {
  color: #9ca3af;
  font-size: 0.8rem;
  user-select: none;
  flex: 1;
  font-style: italic;
}

.dropdown-arrow {
  color: #6b7280;
  font-size: 1rem;
  transition: all 0.2s ease;
  margin-left: 0.5rem;
  flex-shrink: 0;
}

.dropdown-arrow.rotated {
  transform: rotate(180deg);
  color: #8b5cf6;
}

/* MODAL FOOTER */
.modal-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 1.5rem;
  border-top: 1px solid #e2e8f0;
  background: white;
  border-radius: 0 0 12px 12px;
  flex-shrink: 0;
}

.footer-right {
  display: flex;
  gap: 0.75rem;
}

.cancel-btn,
.save-btn {
  padding: 0.625rem 1.25rem;
  border-radius: 6px;
  font-size: 0.8rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
}

.cancel-btn {
  background: white;
  border: 1px solid #e5e7eb;
  color: #374151;
}

.cancel-btn:hover {
  background: #f9fafb;
  border-color: #d1d5db;
}

.save-btn {
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  color: white;
  border: none;
  box-shadow: 0 2px 4px rgba(16, 185, 129, 0.2);
}

.save-btn:hover:not(:disabled) {
  background: linear-gradient(135deg, #059669 0%, #047857 100%);
}

.save-btn:disabled {
  background: #9ca3af;
  cursor: not-allowed;
}

/* DELETE MODAL */
.delete-content {
  display: flex;
  gap: 1rem;
  align-items: flex-start;
}

.delete-icon {
  color: #ef4444;
  font-size: 2rem;
  background: #fef2f2;
  padding: 0.75rem;
  border-radius: 8px;
  flex-shrink: 0;
}

.delete-message h4 {
  margin: 0 0 0.5rem 0;
  color: #1f2937;
  font-size: 1.125rem;
  font-weight: 700;
}

.delete-message p {
  margin: 0 0 0.5rem 0;
  color: #6b7280;
  line-height: 1.4;
  font-size: 0.875rem;
}

.delete-confirm-btn {
  background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
  color: white;
  border: none;
  padding: 0.625rem 1.25rem;
  border-radius: 6px;
  font-size: 0.8rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 0.375rem;
  box-shadow: 0 2px 4px rgba(239, 68, 68, 0.2);
}

.delete-confirm-btn:hover {
  background: linear-gradient(135deg, #dc2626 0%, #b91c1c 100%);
}

/* Responsive Design */
@media (max-width: 768px) {
  .content-layout {
    grid-template-columns: 1fr;
    grid-template-rows: auto 1fr;
  }

  .left-panel {
    max-height: 300px;
    overflow-y: auto;
  }

  .rules-header {
    flex-direction: column;
    align-items: stretch;
    gap: 1rem;
  }

  .header-right {
    flex-wrap: wrap;
  }

  .modal-content {
    width: 95%;
    max-width: 95vw;
    max-height: 85vh;
    margin: 0.5rem;
  }

  .modal-header {
    padding: 1rem;
  }

  .modal-body {
    padding: 1rem;
    max-height: calc(85vh - 140px);
  }

  .modal-footer {
    padding: 1rem;
    flex-direction: column;
    gap: 0.75rem;
    align-items: stretch;
  }

  .footer-right {
    justify-content: center;
  }

  .dropdown-menu {
    max-height: 120px !important;
  }

  .selected-item {
    max-width: 80px;
    font-size: 0.65rem;
  }
}

@media (max-width: 480px) {
  .question-rules-configuration {
    padding: 1rem;
  }

  .modal-content {
    width: 98%;
    max-height: 90vh;
    margin: 0.25rem;
  }

  .modal-body {
    max-height: calc(90vh - 120px);
  }

  .rules-table {
    font-size: 0.75rem;
  }

  .rules-table th,
  .rules-table td {
    padding: 0.5rem;
  }

  .selected-items {
    gap: 0.25rem;
  }

  .selected-item {
    max-width: 70px;
    font-size: 0.6rem;
    padding: 0.2rem 0.4rem;
  }

  .dropdown-menu {
    max-height: 100px !important;
  }
}

/* Focus states for accessibility */
.action-btn:focus,
.add-btn:focus {
  outline: 2px solid #8b5cf6;
  outline-offset: 2px;
}

.modal-input:focus {
  outline: none;
}

.selected-questions:focus {
  outline: 2px solid #8b5cf6;
  outline-offset: 2px;
}

/* Scrollbar improvements */
.dropdown-options::-webkit-scrollbar,
.modal-body::-webkit-scrollbar {
  width: 4px;
}

.dropdown-options::-webkit-scrollbar-track,
.modal-body::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 2px;
}

.dropdown-options::-webkit-scrollbar-thumb,
.modal-body::-webkit-scrollbar-thumb {
  background: #8b5cf6;
  border-radius: 2px;
}

.dropdown-options::-webkit-scrollbar-thumb:hover,
.modal-body::-webkit-scrollbar-thumb:hover {
  background: #7c3aed;
}
</style>
